import React from 'react';
import { List, ListItemButton, Tooltip } from '@mui/material';
import { SnackContext } from 'src/store/ContextStore';
import {
  isPregnancyNode,
  isGameteDonorNode,
  canCarryPregnancy
} from '../../pedigree/config';
import {
  PregnancyModal,
  DeleteConfirm,
  usePregnancyCrud,
  spouseOptionsFor
} from './pregnancyEditor';

// Track B (pregnancy-record model): pregnancies + donors are RECORDS on the carrier
// (Pregnancy/PregnancyChanged), not persons or config nodes; the pedigree DERIVES the
// render nodes from them. This context-menu entry authors those records via the shared
// editor (PregnancyModal + usePregnancyCrud). Add lives on a carrier; Edit/Delete resolve
// the carrier + record behind a DERIVED pregnancy/donor node via its uid (pregnancy uid =
// record._id; donor uid = `<record._id>:<index>`).

// The record _id behind a derived node (pregnancy uid = _id; donor uid = `_id:index`).
const recordIdOf = (node) => {
  const uid = node?.uid == null ? '' : String(node.uid);
  return isGameteDonorNode(node) ? uid.split(':')[0] : uid;
};

// Resolve the carrier MEMBER (from memberRecords, the source of truth) + the pregnancy
// record for a node. For a carrier node → that member + no record (Add). For a derived
// pregnancy/donor node → the member whose pregnancies[] holds the record + the record.
const resolveCarrierAndRecord = (node, memberRecords) => {
  const list = memberRecords || [];
  if (isPregnancyNode(node) || isGameteDonorNode(node)) {
    const rid = recordIdOf(node);
    const carrier = list.find((m) =>
      (m.pregnancies || []).some((p) => String(p._id) === rid)
    );
    const record = carrier?.pregnancies?.find((p) => String(p._id) === rid);
    return { carrier, record };
  }
  const carrier = list.find((m) => m.pid === node.pid) || node;
  return { carrier, record: null };
};

export default function ReproductiveMenu({
  node,
  user,
  memberRecords,
  setMemberRecords,
  handleClose,
  setPedigreeState
}) {
  const { setSnack } = React.useContext(SnackContext);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const { busy, submitPregnancy, deletePregnancy, toggleHidden, convertToLiveBirth } =
    usePregnancyCrud({
      memberRecords,
      setMemberRecords,
      setPedigreeState,
      setSnack,
      user
    });

  const { carrier, record } = resolveCarrierAndRecord(node, memberRecords);
  const isReproNodeSelected = isPregnancyNode(node) || isGameteDonorNode(node);
  const canCarry = canCarryPregnancy(node);
  const spouseOptions = spouseOptionsFor(carrier, memberRecords);

  // Return the result; the modal closes itself via onClose (and may first prompt to add the
  // live-birth child).
  const handleSubmit = (formValues) =>
    submitPregnancy(carrier, record, formValues);

  const closeAll = () => {
    setModalOpen(false);
    handleClose?.();
  };

  const handleDelete = async () => {
    const ok = await deletePregnancy(carrier, record);
    if (ok) {
      setConfirmOpen(false);
      handleClose?.();
    }
  };

  // A visible repro node can be hidden from here; it then can't be right-clicked, so
  // un-hiding is done from the carrier's Reproductive sidebar (which lists hidden records).
  const handleHide = async () => {
    const ok = await toggleHidden(carrier, record);
    if (ok) handleClose?.();
  };

  return (
    <>
      <List>
        {isReproNodeSelected ? (
          <>
            <ListItemButton onClick={() => setModalOpen(true)}>
              Edit pregnancy…
            </ListItemButton>
            <ListItemButton onClick={handleHide} disabled={busy}>
              Hide from pedigree
            </ListItemButton>
            <ListItemButton onClick={() => setConfirmOpen(true)}>
              Delete pregnancy
            </ListItemButton>
          </>
        ) : (
          <Tooltip
            title={
              canCarry
                ? ''
                : 'Set Sex Assigned at Birth to Female before adding a pregnancy'
            }
            placement="right"
          >
            <span style={{ display: 'block' }}>
              <ListItemButton
                onClick={() => setModalOpen(true)}
                disabled={!canCarry}
              >
                Add pregnancy
              </ListItemButton>
            </span>
          </Tooltip>
        )}
      </List>

      {modalOpen && (
        <PregnancyModal
          record={record}
          spouseOptions={spouseOptions}
          onSubmit={handleSubmit}
          onClose={closeAll}
          onConvert={(rec, gender) => convertToLiveBirth(carrier, rec, gender)}
        />
      )}
      {confirmOpen && (
        <DeleteConfirm
          onConfirm={handleDelete}
          onClose={() => setConfirmOpen(false)}
          deleting={busy}
        />
      )}
    </>
  );
}
